import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Clock, LogIn, LogOut, Loader2 } from 'lucide-react';
import { fichajesAPI } from '@/lib/api';
import { toast } from 'sonner';
import { usePolling } from '@/hooks/usePolling';

function fmtMinutes(min) {
  if (!min || min < 0) return '0h 00m';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${m.toString().padStart(2, '0')}m`;
}

/**
 * Widget compacto de fichaje para el sidebar.
 * Muestra estado actual (dentro/fuera) + tiempo trabajado hoy + botón fichar.
 * En modo collapsed solo muestra el icono con punto de estado.
 */
export function FichajeWidget({ collapsed = false }) {
  const [estado, setEstado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchEstado = useCallback(async () => {
    try {
      const { data } = await fichajesAPI.miEstado();
      setEstado(data);
      setError(false);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    fetchEstado();
  }, [fetchEstado]);

  // Polling cada 2 min (suficiente para minutos en pantalla) y pausable
  usePolling(fetchEstado, 120_000, { initialDelay: 1500 });

  const handleFichar = async (tipo) => {
    setLoading(true);
    try {
      await fichajesAPI.registrar(tipo);
      toast.success(tipo === 'entrada' ? 'Entrada registrada' : 'Salida registrada');
      await fetchEstado();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error al fichar';
      if (e?.response?.status === 403) {
        // No tiene fichaje activo: ocultar widget silenciosamente
        setError(true);
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Error al fichar');
      }
    } finally {
      setLoading(false);
    }
  };

  if (error) return null;
  if (!estado) return null;

  const dentro = estado.dentro;
  const minutos = estado.minutos_hoy || 0;

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => handleFichar(dentro ? 'salida' : 'entrada')}
        className="flex items-center justify-center h-9 w-full rounded-md hover:bg-sidebar-hover transition-colors relative"
        title={dentro ? `Trabajando · ${fmtMinutes(minutos)} · Click para fichar salida` : 'Click para fichar entrada'}
        data-testid="fichaje-widget-toggle"
      >
        <Clock className={`w-4 h-4 ${dentro ? 'text-green-400' : 'text-sidebar-muted'}`} />
        <span
          className={`absolute top-1.5 right-2 w-2 h-2 rounded-full ${
            dentro ? 'bg-green-500 animate-pulse' : 'bg-sidebar-muted/50'
          }`}
        />
      </button>
    );
  }

  return (
    <div className="px-2 pt-2 pb-1" data-testid="fichaje-widget">
      <div className="rounded-lg border border-sidebar-border bg-sidebar-hover/40 p-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <span
              className={`w-2 h-2 rounded-full ${
                dentro ? 'bg-green-500 animate-pulse' : 'bg-sidebar-muted/60'
              }`}
            />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted">
              {dentro ? 'Trabajando' : 'Fuera de jornada'}
            </span>
          </div>
          <span className="text-[10px] font-mono text-sidebar-foreground tabular-nums">
            {fmtMinutes(minutos)}
          </span>
        </div>
        <Button
          size="sm"
          variant={dentro ? 'outline' : 'default'}
          className="w-full h-7 text-xs gap-1.5"
          onClick={() => handleFichar(dentro ? 'salida' : 'entrada')}
          disabled={loading}
          data-testid={dentro ? 'fichaje-fichar-salida' : 'fichaje-fichar-entrada'}
        >
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : dentro ? (
            <LogOut className="w-3.5 h-3.5" />
          ) : (
            <LogIn className="w-3.5 h-3.5" />
          )}
          {dentro ? 'Fichar salida' : 'Fichar entrada'}
        </Button>
      </div>
    </div>
  );
}
